import { cloneDeep } from "lodash";

import { Alignment, CellFormats, Edge, ISpreadsheetGrid } from "../../../components/Spreadsheet/ISpreadsheetGrid";
import { DateTimeFormat } from "../../../utils/helpers/format.helper";
import { getQuarterStartEndDates } from "../../../utils/helpers/quarter.helper";
import { generateUUID } from "../../../utils/helpers/uuidGenerator";
import { ClientTransTypeRow } from "../../../utils/types/clientTransMapping.type";
import { AllocationColumnTypes, GlEntry, InvestorCommitment, InvestorRow,PitdSummaryValues, SubmitToPcapTransaction, TransactionColumn as wbTransactionColumn,TransactionTypeQuarterlySum,Workbook, WorkbookVersion } from "../../../utils/types/workbook.type";
import { AllocationColumn, Column, ColWidth, CommitmentDatedColumn, InvestorCommitmentRow, InvestorCommitmentRowType, ITDType, Row, SpreadsheetTemplate, StartEndCols, StartEndRows, TransactionColumn, TransactionColumnDetail } from "./AllocationWorkbook.types";

enum stringConstants {
    dynamicDateStr = '<dynamic date>',
    beginingBalanceStr = 'Beg. Balance',
    endingBalanceStr = 'Ending Balance',
    byQuarterStr = 'BY_QUARTER',
    byYearStr = 'BY_YEAR',
    commitmentCloseOnStartStr = 'Commitment Close on'
}

export class AllocationSpreadsheetManager {
    private template: SpreadsheetTemplate = {
        reportName: { col: '', row: 0 },
        quarterHeaderRow: 0,
        mainHeaderRow: 0,
        investorTypeCol: '',
        investorNameCol: '',
        closeCommitmentCol: '',
        investorCommitmentRows: {
            allCommitmentRows: [],
            typeStartEndRows: new Map<string, StartEndRows>()
        },
        closeCommitmentDatedCols: {
            startEndCols: { startCol: '', endCol: '' },
            columns: []
        },
        allocationsCols: {
            startEndCols: { startCol: '', endCol: '' },
            columns: []
        },
        pitdCols: {
            startEndCols: { startCol: '', endCol: '' },
            columns: []
        },
        transactionCols: new Map<number, TransactionColumn>(),
        ytdCols: {
            startEndCols: { startCol: '', endCol: '' },
            columns: []
        },
        itdCols: {
            startEndCols: { startCol: '', endCol: '' },
            columns: []
        },
        lastRow: 0,
        lastCol: 'A'
    };

    constructor(
        private isNew: boolean, 
        private workbookInfo: Workbook, 
        private grid: ISpreadsheetGrid, 
        private investorCommitments: InvestorCommitment[], 
        private pitdSummaryValues: PitdSummaryValues[],
        private transTypeSums: Map<number, TransactionTypeQuarterlySum[]>,
        loadComplete: () => void)
    {
        const startQuarter = workbookInfo.startQuarterNumber;
        const lastQuarter = workbookInfo.transactionColumns.reduce((prevVal,currVal) => currVal.quarterNumber > prevVal ? currVal.quarterNumber : prevVal, 0);

        this.buildInitialTemplateDataStructures(startQuarter, lastQuarter);

        setTimeout(() => {
            let newInvestors: InvestorCommitment[] = [];
            let hasNewColumns = false;

            if(!this.isNew) {
                newInvestors = this.adjustForInvestorChanges();
                hasNewColumns = this.moveCellsForDatedCommitmentColumns(false, newInvestors.length===0) > 0;
                this.setCloseCommitmentDatedColumns(lastQuarter);
            }

            if(this.isNew || newInvestors.length>0 || hasNewColumns) {
                this.rendeFullGrid(this.isNew, newInvestors, transTypeSums);
                this.renderAllColumnsStyling();
            }

            loadComplete();
        }, 1);
    }

    private buildInitialTemplateDataStructures(startQuarter: number, lastQuarter: number) {
        const grid = this.grid;
        const wb = this.workbookInfo;
        const template = this.template;
        
        this.setInitialStartingValues();

        if(this.isNew) {
            this.setInvestorValuesFromInvestorsCommitments();
        } else {
            this.setInvestorValuesFromWBInvestors();
            this.buildInvestorTypeStartEndRows();
        }

        this.setCloseCommitmentDatedColumns(lastQuarter);
    
        this.setAllocationColumns();

        const lastAllocationCol =  template.allocationsCols.startEndCols.endCol; 

        template.pitdCols.startEndCols.startCol = grid.getNextCol(lastAllocationCol, 2);
        this.setITDColumns(template.pitdCols);

        for(let q = startQuarter; q <= lastQuarter; q++) {
            this.setTransactionColumns(q, true);
        }

        const lastTransactionCol = template.transactionCols.get(lastQuarter)?.startEndCols.endCol as string;

        if(wb.includeYtd) {
            template.ytdCols.startEndCols.startCol = grid.getNextCol(lastTransactionCol, 2);
            this.setITDColumns(template.ytdCols);
        }

        if(wb.includeItd) {
            const lastItdCol = wb.includeYtd ? template.ytdCols.startEndCols.endCol : lastTransactionCol;

            template.itdCols.startEndCols.startCol = grid.getNextCol(lastItdCol, 2);
            this.setITDColumns(template.itdCols);
        }

        this.setInitialLastColAndRow();
    }

   // start render building functions

    private rendeFullGrid(writeToLastTransQuarterUserCells: boolean, newInvestors: InvestorCommitment[], transTypeSums: Map<number, TransactionTypeQuarterlySum[]>) {
        const workbook = this.workbookInfo;
        const template = this.template;
        const firstQuarter = this.getFirstQuarter();
        const lastQuarter = this.getLastQuarter();

        this.renderHeaderInfo(workbook);
        this.renderInvestorColumns();
        this.renderDatedCommitmentColumns();
        this.renderAllocationColumns(template.allocationsCols.columns);
        this.renderITDColumns(template.pitdCols, ITDType.pitd);

        for(let q = firstQuarter; q <= lastQuarter; q++) {
            const ignoreUserCells = !(writeToLastTransQuarterUserCells && (q === lastQuarter));
            
            this.renderTransactionColumns(q, transTypeSums.get(q)!, ignoreUserCells, newInvestors);
        }
        
        if(workbook.includeYtd) this.renderITDColumns(template.ytdCols, ITDType.ytd);
        if(workbook.includeItd) this.renderITDColumns(template.itdCols, ITDType.itd);
    }

    private renderHeaderInfo(workbook: Workbook = this.workbookInfo) {
        const grid = this.grid;
        const template = this.template;
        const lastQuarter = this.getLastQuarter();
        const longStartDateStr = this.getLongDateForQuarter(workbook.startQuarterYear, workbook.frequency === stringConstants.byQuarterStr ? lastQuarter : undefined);
        const value = `${workbook.reportName} - ${longStartDateStr}`;

        grid.setCellValue(template.reportName.row, template.reportName.col, value, CellFormats.Text_Unformatted);
    }

    private renderInvestorColumns() {
        const grid = this.grid;
        const template = this.template;
        const headerRow = this.getMainHeaderRow();
        const typeRows = template.investorCommitmentRows.typeStartEndRows;
        const commitmentRows = template.investorCommitmentRows.allCommitmentRows;
        const closeCol = template.closeCommitmentCol;

        grid.startUpdate();

        grid.setCellValue(headerRow, template.investorTypeCol, 'Type', CellFormats.Text_Unformatted);
        grid.setColWidth(template.investorTypeCol, ColWidth.smallText);
        grid.setCellBorder(headerRow, template.investorTypeCol, Edge.Bottom, 2);

        grid.setCellValue(headerRow, template.investorNameCol, 'Partner Name', CellFormats.Text_Unformatted);
        grid.setColWidth(template.investorNameCol, ColWidth.text);
        grid.setCellBorder(headerRow, template.investorNameCol, Edge.Bottom, 2);

        grid.setCellValue(headerRow, closeCol, 'Close Commitment', CellFormats.Text_Unformatted);
        grid.setColWidth(template.closeCommitmentCol, ColWidth.currency);
        grid.setCellBorder(headerRow, template.closeCommitmentCol, Edge.Bottom, 2);


        const ary = commitmentRows.map(cr => cr.rowType);
        const uniqueAry = Array.from(new Set(ary));

    
        commitmentRows.forEach((investorRow) => {
            const currRow = investorRow.row;
            const typeRow = typeRows.get(investorRow.investorType) as StartEndRows;

            switch(investorRow.rowType) {
                case InvestorCommitmentRowType.Investor: 
                    grid.setCellValue(currRow, template.investorTypeCol, investorRow.investorType, CellFormats.Text_Unformatted);
                    grid.setCellValue(currRow, template.investorNameCol, investorRow.investorName, CellFormats.Text_Unformatted);
                    grid.setCellValue(currRow, template.closeCommitmentCol, investorRow.totalTransactionAmount, CellFormats.Number_Accounting);
                    break;
                case InvestorCommitmentRowType.TypeTotal:
                    grid.setCellValue(currRow, template.investorNameCol, `Total ${investorRow.investorType}`, CellFormats.Text_Unformatted);
                    grid.setCellFormula(currRow, template.closeCommitmentCol, `SUM(${closeCol}${typeRow.startRow}:${closeCol}${typeRow.endRow})`, CellFormats.Number_Accounting);
                    break;
                case InvestorCommitmentRowType.PartnerTotal: {
                    const typeTotalRows = commitmentRows.filter((r) => r.rowType === InvestorCommitmentRowType.TypeTotal).map((r) => `${closeCol}${r.row}`).join(`,`);

                    grid.setCellValue(currRow, template.investorNameCol, 'Total Partners', CellFormats.Text_Unformatted);
                    grid.setCellFormula(currRow, template.closeCommitmentCol, `SUM(${typeTotalRows})`, CellFormats.Number_Accounting);
                    break;
                }
            }
        });

        grid.endUpdate();
    }

    private renderDatedCommitmentColumns() {
        const grid = this.grid;
        const template = this.template;
        const headerRow = this.getMainHeaderRow();
        const typeRows = template.investorCommitmentRows.typeStartEndRows;
        const commitmentRows = template.investorCommitmentRows.allCommitmentRows;
        const closeCommitmentDatedCols = template.closeCommitmentDatedCols;

        grid.ensureColumnIndexes(grid.getLastColumnIndex() + closeCommitmentDatedCols.columns.length);
        
        closeCommitmentDatedCols.columns.forEach(cd => {
            grid.setCellValue(headerRow, cd.col, cd.label, CellFormats.Text_Unformatted);
            grid.setColWidth(cd.col, ColWidth.currency);
            grid.setCellBorder(headerRow, cd.col, Edge.Bottom, 2);
        });

        commitmentRows.forEach((investorRow) => {
            const currRow = investorRow.row;
            const typeRow = typeRows.get(investorRow.investorType) as StartEndRows;

            closeCommitmentDatedCols.columns.forEach((cd, index) => {

                if(investorRow.rowType === InvestorCommitmentRowType.Investor) {
                    const val = investorRow.commitmentDatedColumnAmounts[index];

                    setCellValueIfDifferent(currRow, cd.col, val);
                } else if(investorRow.rowType === InvestorCommitmentRowType.TypeTotal)  {
                    setCellFormulaIfDifferent(currRow, cd.col, `SUM(${cd.col}${typeRow.startRow}:${cd.col}${typeRow.endRow})`);
                } else if(investorRow.rowType === InvestorCommitmentRowType.PartnerTotal)  {
                    const typeTotalRows = commitmentRows.filter((r) => r.rowType === InvestorCommitmentRowType.TypeTotal).map((r) => `${cd.col}${r.row}`).join(`,`);

                    setCellFormulaIfDifferent(currRow, cd.col, `SUM(${typeTotalRows})`);
                }

            });

        });

        this.renderSpacerColumn(grid.getNextCol(closeCommitmentDatedCols.startEndCols.endCol));

        function setCellValueIfDifferent(row: Row, col: Column, value: number) {
            let oldVal = grid.getCellValue(row, col);

            oldVal = oldVal ? oldVal : 0;

            if(oldVal !== value) {
                grid.setCellValue(row, col, value, CellFormats.Number_Accounting);
            }
        }

        function setCellFormulaIfDifferent(row: Row, col: Column, formula: string) {
            const oldFormula = grid.getCellFormula(row, col);

            if(oldFormula !== formula) {
                grid.setCellFormula(row, col, formula, CellFormats.Number_Accounting);
            }
        }
    }

    private renderAllocationColumns(allocationsCols: AllocationColumn[]) {
        const grid = this.grid;
        const typeRows = this.template.investorCommitmentRows.typeStartEndRows;
        const commitmentRows = this.template.investorCommitmentRows;
        const allCommitmentRows = commitmentRows.allCommitmentRows;
        const closeCol = this.template.closeCommitmentCol;
        const totalPartnerRow = allCommitmentRows.find(x => x.rowType === InvestorCommitmentRowType.PartnerTotal) as InvestorCommitmentRow;
        const totalTypeRows = allCommitmentRows.filter(x => x.rowType === InvestorCommitmentRowType.TypeTotal) as InvestorCommitmentRow[];
        const headerRow = this.getMainHeaderRow();

        grid.startUpdate();

        allocationsCols.forEach((allocCol) => {
            allocCol.label = this.getAllocationColumnTitle(allocCol.label);

            this.grid.setCellValue(headerRow, allocCol.col, allocCol.label, CellFormats.Text_Unformatted);
            
            commitmentRows.allCommitmentRows.forEach((commitmentRow) => {
                const currRow = commitmentRow.row;
                const typeRow = typeRows.get(commitmentRow.investorType) as StartEndRows;

                if(commitmentRow.rowType === InvestorCommitmentRowType.TypeTotal) {
                    this.grid.setCellBorder(commitmentRow.row, allocCol.col, Edge.Top, 2);
                    this.grid.setCellBorder(commitmentRow.row, allocCol.col, Edge.Bottom, 2);
                } else if(commitmentRow.rowType === InvestorCommitmentRowType.PartnerTotal) {
                    this.grid.setCellBorder(commitmentRow.row, allocCol.col, Edge.Top, 3);
                    this.grid.setCellBorder(commitmentRow.row, allocCol.col, Edge.Bottom, 3);
                }

                let formula: string = '';

                if(allocCol.colType !== AllocationColumnTypes.CUSTOM_FIELD) {

                    switch(commitmentRow.rowType) {
                        case InvestorCommitmentRowType.Investor: 
                            if(allocCol.code === 'COMMITMENT_PERCENT') {
                                formula = `IF(${closeCol}${totalPartnerRow.row}, ${closeCol}${currRow}/${closeCol}${totalPartnerRow.row}, 0)`;
                            } else if(allocCol.colType === AllocationColumnTypes.INVESTMENT_TYPE) {

                                if(allocCol.code === commitmentRow.investorType) {
                                    const partnerTypeRow = totalTypeRows.find((x) => x.investorType === allocCol.code) as InvestorCommitmentRow;
                                    
                                    formula = `IF(${closeCol}${partnerTypeRow.row}, ${closeCol}${currRow}/${closeCol}${partnerTypeRow.row}, 0 )`;
                                } else {
                                    formula = '';
                                }

                            }
                            break;
                        case InvestorCommitmentRowType.TypeTotal:
                            formula = `SUM(${allocCol.col}${typeRow.startRow}:${allocCol.col}${typeRow.endRow})`;
                            break;
                        case InvestorCommitmentRowType.PartnerTotal: {
                            const typeTotalRows = allCommitmentRows.filter((r) => r.rowType === InvestorCommitmentRowType.TypeTotal).map((r) => `${allocCol.col}${r.row}`).join(`,`);
        
                            formula = `SUM(${typeTotalRows})`;
                            break;
                        }
                    }

                }

                if(formula) {
                    if(!grid.getCellFormula(commitmentRow.row, allocCol.col)) {
                        grid.setCellFormula(commitmentRow.row, allocCol.col, formula, CellFormats.Number_Percent_2);
                    }
                } else {
                    if(!grid.getCellValue(commitmentRow.row, allocCol.col)) {
                        grid.setCellValue(commitmentRow.row, allocCol.col, 0, CellFormats.Number_Percent_2);
                    }
                }
            });

        });

        grid.endUpdate();
    }

    private renderTransactionColumns(quarterNumber: number, transtypeSums:TransactionTypeQuarterlySum[], ignoreUserRows: boolean, investorRowsToForceWrite: InvestorCommitment[]) {
        const _this = this;
        const grid = this.grid;
        const workbook = this.workbookInfo;
        const year = workbook.startQuarterYear;
        const template = this.template;
        const headerRow = this.getMainHeaderRow();
        const typeRows = template.investorCommitmentRows.typeStartEndRows;
        const transactionCols = template.transactionCols.get(quarterNumber) as TransactionColumn;
        const commitmentRows = template.investorCommitmentRows.allCommitmentRows;
        const totalPartnerCommitmentRow = commitmentRows.find((x) => x.rowType === InvestorCommitmentRowType.PartnerTotal) as InvestorCommitmentRow;
        const totalPartnerRow = totalPartnerCommitmentRow.row;
        const qtrDateStr = workbook.frequency === stringConstants.byQuarterStr ? `QTD Q${quarterNumber}-${year}` : `YTD ${year}`;

        grid.startUpdate();

        transactionCols.columns.forEach((transactionCol, _index, transColDetails) => {
            grid.setCellValue(headerRow, transactionCol.col, transactionCol.label, CellFormats.Text_Unformatted);

            const allocCol = transactionCol.allocationCol as AllocationColumn;

            commitmentRows.forEach((cr) => {
                const ignoreUserRow = ignoreUserRows && !investorRowsToForceWrite.find(i => i.investorId === cr.investorId);

                if(cr.rowType === InvestorCommitmentRowType.TypeTotal) {
                    this.grid.setCellBorder(cr.row, transactionCol.col, Edge.Top, 2);
                    this.grid.setCellBorder(cr.row, transactionCol.col, Edge.Bottom, 2);
                } else if(cr.rowType === InvestorCommitmentRowType.PartnerTotal) {
                    this.grid.setCellBorder(cr.row, transactionCol.col, Edge.Top, 3);
                    this.grid.setCellBorder(cr.row, transactionCol.col, Edge.Bottom, 3);
                }

                if(transactionCol.label === stringConstants.beginingBalanceStr) {
                    const firstQuarter = this.getFirstQuarter();
                    let prevEndBalance;
                    
                    if(workbook.frequency === stringConstants.byYearStr || quarterNumber === firstQuarter) {
                        prevEndBalance = template.pitdCols.startEndCols.endCol;
                    } else {
                        const prevTransactionCols = this.template.transactionCols.get(quarterNumber-1) as TransactionColumn;

                        prevEndBalance = prevTransactionCols.startEndCols.endCol;
                    }

                    grid.setCellFormula(cr.row, transactionCol.col, `${prevEndBalance}${cr.row}`, CellFormats.Number_Accounting);

                    return;
                } else if(transactionCol.label === stringConstants.endingBalanceStr) {
                    const lastCalcCol = grid.getPrevCol(transactionCols.startEndCols.endCol);
                    const endBalanceFormula = `SUM(${transactionCols.startEndCols.startCol}${cr.row}:${lastCalcCol}${cr.row})`;

                    grid.setCellFormula(cr.row, transactionCol.col, endBalanceFormula, CellFormats.Number_Accounting);

                    if(cr.rowType === InvestorCommitmentRowType.PartnerTotal) {
                        const endBalanceFormula = `SUM(${transactionCols.startEndCols.startCol}${cr.row}:${lastCalcCol}${cr.row+1})`;

                        grid.setCellFormula(cr.row+1, transactionCol.col, endBalanceFormula, CellFormats.Number_Accounting);
                    }

                    return;
                }

                const typeRow = typeRows.get(cr.investorType) as StartEndRows;

                switch(cr.rowType) {
                    case InvestorCommitmentRowType.Investor: {
                        if(ignoreUserRow) break;

                        const roundInfo = getCellRoundingInfo(transactionCol.transTypeId, cr.row);
                        
                        if(transactionCol.useArkTransactionValues) {
                            const transTypeSum = transtypeSums.find(t => cr.investorId === t.investorId && transactionCol.transTypeId === t.transTypeId);
                            let transTypeSumVal: number | undefined;

                            if(transTypeSum?.sum || !isNaN(transTypeSum?.sum as number)) {
                                transTypeSumVal = transactionCol.metricSign === -1 ? transTypeSum?.sum! * -1 : transTypeSum!.sum!;
                            } else {
                                transTypeSumVal = undefined;
                            }

                            if(roundInfo.isRoundingFormula) {
                                grid.setCellFormula(cr.row, transactionCol.col, `Round(${transTypeSumVal ?? 0}, ${roundInfo.decimalPoints})`, CellFormats.Number_Accounting);
                            } else {
                                grid.setCellValue(cr.row, transactionCol.col, transTypeSumVal ?? '', CellFormats.Number_Accounting);
                            }
                        } else if(allocCol?.col) {
                            const allocColFormula = `${allocCol.col}${cr.row}*$${transactionCol.col}$${totalPartnerRow}`;

                            if(roundInfo.isRoundingFormula) {
                                grid.setCellFormula(cr.row, transactionCol.col, `Round(${allocColFormula}, ${roundInfo.decimalPoints})`, CellFormats.Number_Accounting);
                            } else {
                                grid.setCellFormula(cr.row, transactionCol.col, allocColFormula, CellFormats.Number_Accounting);
                            }
                        } else if(roundInfo.isRoundingFormula && roundInfo.formula) {
                            grid.setCellFormula(cr.row, transactionCol.col, roundInfo.formula, CellFormats.Number_Accounting);
                        } else {
                            if(grid.getCellValue(cr.row, transactionCol.col) !== '') {
                                grid.setCellValue(cr.row, transactionCol.col, '', CellFormats.Text_Unformatted);
                            }
                        }

                        break;
                    }
                    case InvestorCommitmentRowType.TypeTotal:
                        grid.setCellFormula(cr.row, transactionCol.col, `SUM(${transactionCol.col}${typeRow.startRow}:${transactionCol.col}${typeRow.endRow})`, CellFormats.Number_Accounting);
                        break;
                    case InvestorCommitmentRowType.PartnerTotal: {
                        const typeTotalRows = commitmentRows.filter(r => r.rowType === InvestorCommitmentRowType.TypeTotal).map((r) => `${transactionCol.col}${r.row}`).join(`,`);
                        const sumFormula = `SUM(${typeTotalRows})`;

                        if(transactionCol.useArkTransactionValues) {
                            grid.setCellFormula(cr.row, transactionCol.col, sumFormula, CellFormats.Number_Accounting);
                        } else { 
                            if(!ignoreUserRow && grid.getCellValue(cr.row, transactionCol.col) !== '') {
                                grid.setCellValue(cr.row, transactionCol.col, '', CellFormats.Number_Accounting);
                            }
                        }

                        // Partner total reconciler row
                        grid.setCellFormula(cr.row+1, transactionCol.col, `${sumFormula}-${transactionCol.col}${cr.row}`, CellFormats.Number_Accounting);
                        break;
                    }
                }
            });

        });

        grid.setCellValue(template.quarterHeaderRow, transactionCols.startEndCols.startCol, qtrDateStr, CellFormats.Text_Unformatted);
        grid.setSpanRange(template.quarterHeaderRow, transactionCols.startEndCols.startCol, template.quarterHeaderRow, transactionCols.startEndCols.endCol);
        grid.setCellAlign(template.quarterHeaderRow, transactionCols.startEndCols.startCol, Alignment.Center);

        grid.endUpdate();

        function getCellRoundingInfo(transTypeId: string|undefined, row: Row): {isRoundingFormula: boolean, formula: string|undefined, decimalPoints: number} {
            if(!transTypeId) {
                return { isRoundingFormula: false, formula: undefined, decimalPoints: 0 };
            }

            if(quarterNumber === _this.getFirstQuarter()) {
                const transType = template.transactionCols.get(quarterNumber)!.columns.find(c => c.transTypeId === transTypeId)!;

                if(_this.workbookInfo.decimalRounding > -1 && (transType.allocationCol || transType.useArkTransactionValues)) {
                    return { isRoundingFormula: true, formula: undefined, decimalPoints: _this.workbookInfo.decimalRounding };
                }

                return { isRoundingFormula: false, formula: undefined, decimalPoints: 0 };
            }

            const prevTransType = template.transactionCols.get(quarterNumber-1)!.columns.find(c => c.transTypeId === transTypeId)!;

            if(!prevTransType.col) {
                return { isRoundingFormula: false, formula: undefined, decimalPoints: 0 };
            }

            const actualVal = grid.getCellFormula(row, prevTransType.col);

            if(!actualVal) {
                return { isRoundingFormula: false, formula: undefined, decimalPoints: 0 };
            }

            const isRound = actualVal.toLowerCase().startsWith('round');

            if(!isRound) {
                return { isRoundingFormula: false, formula: actualVal, decimalPoints: 0 };
            }

            const lastCloseParanIdx = actualVal.lastIndexOf(')');
            const decimalPoint = actualVal[lastCloseParanIdx-1];

            return { isRoundingFormula: true, formula: actualVal, decimalPoints: parseInt(decimalPoint) };
        }
    }

    private renderITDColumns(itdCols: TransactionColumn, itdType: ITDType) {
        const grid = this.grid;
        const template =  this.template;
        const typeRows = template.investorCommitmentRows.typeStartEndRows;
        const headerRow = this.getMainHeaderRow();
        const transactionCols = this.template.transactionCols;
        const commitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
        const pitaStartCol = template.pitdCols.startEndCols.startCol;
        const startYear = this.workbookInfo.startQuarterYear;
        const titleStr = itdType === ITDType.pitd ? 'PITD ' + (startYear-1) : itdType === ITDType.ytd ? 'YTD ' + startYear : 'ITD ' + startYear; 

        grid.startUpdate();

        itdCols.columns.forEach((itdCol, colIdx) => {
            this.setCurrencyColStyles(itdCol.col);
            grid.setCellValue(headerRow, itdCol.col, itdCol.label, CellFormats.Text_Unformatted);
            
            commitmentRows.forEach((cr) => {
                if(cr.rowType === InvestorCommitmentRowType.TypeTotal) {
                    this.grid.setCellBorder(cr.row, itdCol.col, Edge.Top, 2);
                    this.grid.setCellBorder(cr.row, itdCol.col, Edge.Bottom, 2);
                } else if(cr.rowType === InvestorCommitmentRowType.PartnerTotal) {
                    this.grid.setCellBorder(cr.row, itdCol.col, Edge.Top, 3);
                    this.grid.setCellBorder(cr.row, itdCol.col, Edge.Bottom, 3);
                }

                if(itdCol.label === stringConstants.beginingBalanceStr) {
                    if(itdType === ITDType.ytd) {
                        grid.setCellFormula(cr.row, itdCol.col, `${template.pitdCols.startEndCols.endCol}${cr.row}`, CellFormats.Number_Accounting);
                    } else {
                        grid.setCellValue(cr.row, itdCol.col, 0, CellFormats.Number_Accounting);
                    }
                    return;
                }

                if(itdCol.label === stringConstants.endingBalanceStr) {
                    const lastCalcCol = grid.getPrevCol(itdCols.startEndCols.endCol);
                    const endBalanceFormula = `SUM(${itdCols.startEndCols.startCol}${cr.row}:${lastCalcCol}${cr.row})`;

                    grid.setCellFormula(cr.row, itdCol.col, endBalanceFormula, CellFormats.Number_Accounting);

                    return;
                }

                const typeRow = typeRows.get(cr.investorType) as StartEndRows;

                switch(itdType) {
                    case ITDType.pitd: {

                        switch(cr.rowType) {
                            case InvestorCommitmentRowType.Investor: {
                                const pitdInvestor = this.pitdSummaryValues.find(investor => cr.investorId === investor.investorId);

                                if(pitdInvestor?.transactionTotalMap) {
                                    let amount = pitdInvestor.transactionTotalMap[itdCol.label];

                                    if(!isNaN(amount)) {
                                        amount = itdCol.metricSign === -1 ? amount * -1 : amount;

                                        grid.setCellValue(cr.row, itdCol.col, amount, CellFormats.Number_Accounting);
                                    }
                                }
                                break;
                            }
                            case InvestorCommitmentRowType.TypeTotal: 
                                grid.setCellFormula(cr.row, itdCol.col, `SUM(${itdCol.col}${typeRow.startRow}:${itdCol.col}${typeRow.endRow})`, CellFormats.Number_Accounting);
                            break;
                            case InvestorCommitmentRowType.PartnerTotal: {
                                const typeTotalRows = commitmentRows.filter(r => r.rowType === InvestorCommitmentRowType.TypeTotal).map(r => `${itdCol.col}${r.row}`).join(`,`);

                                grid.setCellFormula(cr.row, itdCol.col, `SUM(${typeTotalRows})`, CellFormats.Number_Accounting);
                            }
                            break;
                        }

                        break;
                    }
                    case ITDType.ytd: 
                    case ITDType.itd: {
                        const transactionColsValues: string[] = [];

                        transactionCols.forEach(rc => {
                            transactionColsValues.push( 
                                `${grid.getNextCol(rc.startEndCols.startCol, colIdx)}${cr.row}`
                             );
                        });

                        let formula = `SUM(${transactionColsValues.join(',')})`;
                        
                        if(itdType === ITDType.itd) {
                            const pitaCol = grid.getNextCol(pitaStartCol, colIdx);

                            formula += `+${pitaCol}${cr.row}`;
                        }

                        grid.setCellFormula(cr.row, itdCol.col, formula, CellFormats.Number_Accounting);
                        break;
                    }
                }
            });

        });

        grid.setCellValue(template.quarterHeaderRow, itdCols.startEndCols.startCol, `${titleStr}`, CellFormats.Text_Unformatted);
        grid.setSpanRange(template.quarterHeaderRow, itdCols.startEndCols.startCol, template.quarterHeaderRow, itdCols.startEndCols.endCol);
        grid.setCellAlign(template.quarterHeaderRow, itdCols.startEndCols.startCol, Alignment.Center);

        grid.endUpdate();
    }

    private adjustForInvestorChanges() {
        const grid = this.grid;
        const template = this.template;
        const investorCommitments = this.investorCommitments;
        const investorCommitmentRows = this.template.investorCommitmentRows;
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
        const investorNameCol = template.investorNameCol;

        
        investorCommitments.forEach(ir => {
            const cr = allCommitmentRows.find(cr => cr.investorId === ir.investorId);
            
            if(!cr) return;

            const currWbName = grid.getCellValue(cr.row, investorNameCol);

            if(currWbName !== ir.investorName) {
                cr.investorName = ir.investorName;
                grid.setCellValue(cr.row, investorNameCol, cr.investorName, CellFormats.Text_Unformatted);
            }
        });

        
        // find investors that have name change or have been removed
        allCommitmentRows.filter(cr => cr.rowType === InvestorCommitmentRowType.Investor).forEach(investorRow => {
            const investorCommitment = investorCommitments.find(ic => ic.investorId === investorRow.investorId);

            if(investorCommitment) {
                investorRow.investorName = investorCommitment.investorName;
                investorRow.deleted = false;
            } else {
                investorRow.deleted = true;
            }
        });

        // find investors that have been added
        const newInvestors = investorCommitments.filter(ic => !allCommitmentRows.find(cr => cr.investorId === ic.investorId));

        if(!newInvestors?.length) return [];

        this.clearSumFormulas('A');

        grid.ensureRowIndexes(allCommitmentRows.length + newInvestors.length+20);

        newInvestors.forEach(newInvestor => {
            let investorToInsertBefore: InvestorCommitmentRow | undefined = undefined;

            const allInvestorsofType = 
                allCommitmentRows.filter(cr => cr.rowType === InvestorCommitmentRowType.Investor && newInvestor.investorType === cr.investorType);

            if(allInvestorsofType?.length) {
                investorToInsertBefore = allInvestorsofType.find(cr => cr.investorName > newInvestor.investorName);
            }

            let insertRow: number;
            let moveSize: number;
            let newInvestorTypeSum: InvestorCommitmentRow|undefined = undefined;

            if(investorToInsertBefore) {
                insertRow = investorToInsertBefore.row;
                moveSize = 1;
            } else { 
                // if investorToInsertBefore = undefined, then it must be last in type section or a new type
                const typeStartEndRow = investorCommitmentRows.typeStartEndRows.get(newInvestor.investorType);

                if(typeStartEndRow) {
                    // end of existing type section
                    insertRow = typeStartEndRow.endRow+1;
                    moveSize = 1;
                } else {
                    //  need new type section
                    const types = Array.from( investorCommitmentRows.typeStartEndRows.keys() )
                        .filter(t => t !== 'LP') // LP is always first so wont deal with it
                        .sort((a, b) => a < b ? -1 : 1);

                    const typeToInsertBefore = types.reverse().find(t => newInvestor.investorType > t);

                    if(typeToInsertBefore) {
                        const tempTypeStartEndRow = investorCommitmentRows.typeStartEndRows.get(typeToInsertBefore)!;

                        insertRow = tempTypeStartEndRow.startRow;
                    } else {
                        // if we are here then we'll use the Partners Total
                        insertRow = allCommitmentRows.find(cr => cr.rowType === InvestorCommitmentRowType.PartnerTotal)!.row;
                    }

                    moveSize = 4;

                    newInvestorTypeSum = this.getNewInvestorTypeSum(
                        undefined,
                        newInvestor.investorType,
                        insertRow+2,
                        InvestorCommitmentRowType.TypeTotal
                    );
                }
            }

            allCommitmentRows.filter(cr => cr.row >= insertRow).forEach(cr => { cr.row += moveSize; });

            allCommitmentRows.push({
                investorId: newInvestor.investorId,
                investorName: newInvestor.investorName,
                investorType: newInvestor.investorType,
                isNew: true,
                deleted: false,
                row: insertRow,
                rowType: InvestorCommitmentRowType.Investor,
                commitmentDatedColumnAmounts: [],
                totalTransactionAmount: 0
            });

            if(newInvestorTypeSum) {
                allCommitmentRows.push(newInvestorTypeSum);
            }

            allCommitmentRows.sort((a,b) => a.row < b.row ? -1 : 1);
            
            this.buildInvestorTypeStartEndRows();

            this.template.lastRow+=moveSize;


            grid.moveCells({
                    startCol: template.investorTypeCol, 
                    endCol: this.getLastCol(),
                    startRow: insertRow,
                    endRow: this.getLastRow()
                },
                insertRow+moveSize, template.investorTypeCol
            );

        });

        return newInvestors;
    }


    private clearSumFormulas(startCol: Column) {
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
        const sumRows = allCommitmentRows.filter(cr => cr.rowType !== InvestorCommitmentRowType.Investor);
        const grid = this.grid;
        const lastCol = this.getLastCol();

        // because TG sucks we need to clear formula becuase it doesnt always adjust correctly.  
        // They will get reset later in rendeFullGrid()
        sumRows.forEach(sumRow => {
            let currCol = startCol;

            while( (this.compareCols(currCol, lastCol) === -1) ) {
                const formula = grid.getCellFormula(sumRow.row, currCol);

                if(formula) {
                    grid.setCellValue(sumRow.row, currCol, '', CellFormats.Text_Unformatted);
                }

                currCol = grid.getNextCol(currCol);
            }
        });
    }



    private renderAllColumnsStyling() {
        const wb = this.workbookInfo;
        const template = this.template;
        const firstQuarter = this.getFirstQuarter();
        const lastQuarter = this.getLastQuarter();

        this.renderDatedCommitmentColumnsStyling();
        this.renderAllocationColumnsStyling();
        this.renderITDColumnsStyling(template.pitdCols);

        for(let q=firstQuarter; q <= lastQuarter; q++) {
            this.renderTransactionColumnsStyling(q);
        }

        if(wb.includeYtd) {
            this.renderITDColumnsStyling(template.ytdCols);
        }

        if(wb.includeItd) {
            this.renderITDColumnsStyling(template.itdCols);
        }

        this.renderCleanup();
    }

    private renderDatedCommitmentColumnsStyling() {
        this.template.closeCommitmentDatedCols.columns.forEach(datedCommitCol => {
            this.setCurrencyColStyles(datedCommitCol.col);
        });

        this.renderSpacerColumn(this.grid.getNextCol(this.template.closeCommitmentDatedCols.startEndCols.endCol));
    }

    private renderAllocationColumnsStyling() {
        this.template.allocationsCols.columns.forEach(allocCol => {
            this.setCurrencyColStyles(allocCol.col);
        });

        this.renderSpacerColumn(this.grid.getNextCol(this.template.allocationsCols.startEndCols.endCol));
    }

    private renderITDColumnsStyling(itdCols: TransactionColumn) {
        itdCols.columns.forEach((itdCol) => {
            this.setCurrencyColStyles(itdCol.col);
        });

        this.renderSpacerColumn(this.grid.getNextCol(itdCols.startEndCols.endCol));
    }

    public renderGlEntries(glEntries: GlEntry[]) {
        const grid = this.grid;
        const transactionCols = this.template.transactionCols;
        const commitmentRows = this.template.investorCommitmentRows.allCommitmentRows;

        const partnerTotalRow = commitmentRows.find(cr => cr.rowType === InvestorCommitmentRowType.PartnerTotal)!.row;
        const firstQuarter = this.getFirstQuarter();
        const lastQuarter = this.getLastQuarter();

        for(let q = firstQuarter; q <= lastQuarter; q++) {
            const currQuarterTransCols = transactionCols.get(q)?.columns as TransactionColumnDetail[];

            currQuarterTransCols.filter(c => !c.useArkTransactionValues).forEach(transCol => {
                const glEntry = glEntries.find(entry => entry.transactionTypeId === transCol.transTypeId && entry.quarter === q);

                if(glEntry && !transCol.locked) {
                    grid.setCellValue(partnerTotalRow, transCol.col, glEntry?.amount, CellFormats.Number_Accounting);
                }
            });           
        }
    }

    private renderTransactionColumnsStyling(quarterNumber: number) {
        const transactionCols = this.template.transactionCols.get(quarterNumber) as TransactionColumn;

        transactionCols.columns.forEach((transactionCol) => {
            this.setCurrencyColStyles(transactionCol.col);
        });

        this.renderSpacerColumn(this.grid.getNextCol(transactionCols.startEndCols.endCol));
    }

    private renderCleanup() {
        const grid = this.grid;

        setTimeout(() => {
            grid.AutoUpdateRowHeight(this.getMainHeaderRow());
        }, 500);
    }

    private renderSpacerColumn(col: Column) {
        const grid = this.grid;
        const template = this.template;
        const rows = template.investorCommitmentRows.allCommitmentRows;

        rows.forEach((investorRow) => {
            if(grid.getCellValue(investorRow.row, col) !== '') {
                grid.setCellValue(investorRow.row, col, '', CellFormats.Text_Unformatted);
            }
        });

        grid.setColWidth(col, ColWidth.spacer);
    }

    // start render building functions


    // start Data building functions

    private setInitialStartingValues() {
        const template = this.template;
        
        template.reportName = { col: 'A', row: 1 };
        
        template.quarterHeaderRow = 3;
        template.mainHeaderRow = 4;

        template.investorTypeCol = 'A';
        template.investorNameCol = 'B';
        template.closeCommitmentCol = 'C';
    }

    private setInvestorValuesFromInvestorsCommitments() {
        const template = this.template;
        const investorTypeRows = template.investorCommitmentRows.typeStartEndRows;
        
        const investorCommitmentRows = this.investorCommitments.map(investorCommitment => {

            return {
                ... investorCommitment, 
                row: 0, 
                rowType: InvestorCommitmentRowType.Investor,
                isNew: false,
                totalTransactionAmount: 0, // needs to be set in setCloseCommitmentDatedColumns()
                commitmentDatedColumnAmounts: [], // needs to be set in setCloseCommitmentDatedColumns()
                deleted: false // set in adjustForInvestorChanges()
            } as InvestorCommitmentRow; 
        });

        investorCommitmentRows.sort((a, b) => {
            if(a.investorType === b.investorType) {
                return a.investorName < b.investorName ? -1 : 1;
            }

            //force LP on top
            if(a.investorType === 'LP') return -1;
            if(b.investorType === 'LP') return 1;

            return a.investorType < b.investorType ? -1 : 1;
        }) ;


        const startInvestRow = this.getMainHeaderRow() + 1;

        let currInvestRow = startInvestRow;
        let currInvestTypeStart = currInvestRow;

        for(let i = 0; i < investorCommitmentRows.length; i++) {
            const investor = investorCommitmentRows[i];

            investor.row = currInvestRow;
            investor.rowType = InvestorCommitmentRowType.Investor;

            const nextInvestorType = i+1 >= investorCommitmentRows.length ? undefined : investorCommitmentRows[i+1].investorType;
            const isLastCurrType = investor.investorType !== nextInvestorType;

            if(isLastCurrType) {
               investorTypeRows.set(investor.investorType, { startRow: currInvestTypeStart, endRow: currInvestRow });
                currInvestRow+=2;

                const newInvestorTypeSum = this.getNewInvestorTypeSum(undefined, investor.investorType, currInvestRow, InvestorCommitmentRowType.TypeTotal);

                if(investorCommitmentRows.length > i + 1) {
                    currInvestTypeStart = currInvestRow+2;
                    investorCommitmentRows.splice(i+1, 0, newInvestorTypeSum);
                } else {
                    investorCommitmentRows.push(newInvestorTypeSum);
                }

                i++;
                currInvestRow++;
            }

            currInvestRow++;
        }

        investorCommitmentRows.push(this.getNewInvestorTypeSum(undefined, 'Partners', currInvestRow, InvestorCommitmentRowType.PartnerTotal));

        this.template.investorCommitmentRows.allCommitmentRows = investorCommitmentRows;
    }

    private setInvestorValuesFromWBInvestors() { 
        const wbInvestorRows = this.workbookInfo.investorRows.sort((a, b) => a.row < b.row ? -1 : 1);

        const investorCommitmentRows = wbInvestorRows.map(wbInvestorRow => {

            if(wbInvestorRow.rowType as InvestorCommitmentRowType === InvestorCommitmentRowType.Investor) {
                return {
                    id: wbInvestorRow.id,
                    investorId: wbInvestorRow.investorId,
                    investorName: wbInvestorRow.investorName,
                    investorType: wbInvestorRow.investorType,
                    row: wbInvestorRow.row, 
                    rowType: InvestorCommitmentRowType.Investor,
                    isNew: false,
                    totalTransactionAmount: 0,  // needs to be set in setCloseCommitmentDatedColumns()
                    commitmentDatedColumnAmounts: [], // needs to be set in setCloseCommitmentDatedColumns()
                    deleted: wbInvestorRow.deleted
                } as InvestorCommitmentRow; 
            } else {
                return this.getNewInvestorTypeSum(wbInvestorRow.id, wbInvestorRow.investorType, wbInvestorRow.row, wbInvestorRow.rowType);
            }
        });

        this.template.investorCommitmentRows.allCommitmentRows = investorCommitmentRows;
    }

    private buildInvestorTypeStartEndRows() {
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
        const filteredRows = allCommitmentRows.filter(cr => cr.rowType !== InvestorCommitmentRowType.PartnerTotal);
        const investorTypeRows = this.template.investorCommitmentRows.typeStartEndRows;

        const uniqueTypes = Array.from(new Set( filteredRows.map(cr => cr.investorType) ));

        uniqueTypes.forEach(t => {
            const commitmentRowsForType = filteredRows.filter(cr => cr.rowType === InvestorCommitmentRowType.Investor && cr.investorType === t);
            const startRow = commitmentRowsForType.reduce((a, b) =>  b.row < a ? b.row : a, Number.MAX_VALUE);
            const endRow = commitmentRowsForType.reduce((a, b) => b.row > a ? b.row : a, 0);

            investorTypeRows.set(t, { startRow, endRow });
        });
    }

    private getNewInvestorTypeSum(id:string|undefined, investorType: string, currInvestRow: Row, rowType: InvestorCommitmentRowType) {
        const newInvestorTypeSum: InvestorCommitmentRow = {
            id: id,
            investorId: '-',
            investorName: '-',
            investorType:  investorType,
            rowType: rowType,
            row: currInvestRow,
            totalTransactionAmount: 0,
            isNew: false,
            commitmentDatedColumnAmounts: [],
            deleted: false // set in adjustForInvestorChanges()
        };

        return newInvestorTypeSum;
    }

    private setAllRowsValues(col: Column, value: string | number, cdllFormat: CellFormats ) {
        const grid = this.grid;
        const rows = this.template.investorCommitmentRows.allCommitmentRows;

        rows.forEach((currRow) => {
            grid.setCellValue(currRow.row, col, value, cdllFormat);
        });
    }

    private setCurrencyColStyles(col: Column) {
        const grid = this.grid;
        const headerRow = this.getMainHeaderRow();

        grid.setColWidth(col, ColWidth.currency);
        grid.setCellAlign(headerRow, col, Alignment.Center);
        grid.setCellWordWrap(headerRow, col, true);
        grid.setCellBold(headerRow, col, true);

        grid.setCellBorder(headerRow, col, Edge.Bottom, 2);
    }
    
    private setCloseCommitmentDatedColumns(endQuarterNumber: number) {
        const grid = this.grid;
        const wb = this.workbookInfo;
        const template = this.template;
        const allCommitmentRows = template.investorCommitmentRows.allCommitmentRows;
        const quarterStartEndDates = getQuarterStartEndDates(endQuarterNumber, wb.startQuarterYear);
        const uniqueCommitmentDates = this.getUniqueCommitmentDates(this.investorCommitments, new Date(0), quarterStartEndDates.endDate);

        const commitmentDatedCols = template.closeCommitmentDatedCols;
        const startCol = grid.getNextCol(template.closeCommitmentCol);

        commitmentDatedCols.columns = [];

        let currCol = startCol;
        
        uniqueCommitmentDates.forEach(ccd => {
            const longStartDateStr = DateTimeFormat.longDate(ccd);

            commitmentDatedCols.columns.push({
                label: `${stringConstants.commitmentCloseOnStartStr} ${longStartDateStr}`,
                commitmentDate: ccd,
                col: currCol
            } as CommitmentDatedColumn);

            currCol = grid.getNextCol(currCol);
        });

        const totalCommitmentThruQuarterDate = getQuarterStartEndDates(wb.startQuarterNumber, wb.startQuarterYear).endDate;

        commitmentDatedCols.startEndCols.startCol = startCol;
        commitmentDatedCols.startEndCols.endCol = grid.getPrevCol(currCol);

        allCommitmentRows.forEach(cr => {
            const investorCommitment = this.investorCommitments.find(ic => ic.investorId === cr.investorId);

            if(!investorCommitment) return;

            const transDetails = investorCommitment.transactionsDetails;

            const amountSums = uniqueCommitmentDates.map((cd, _index) => {
                const detailsByDate = transDetails.filter(td => DateTimeFormat.getDateOnly(td.date).getTime() === cd.getTime());
                const sum = detailsByDate.reduce((a,b) => a + b.amount, 0);

                return sum;
            });

            cr.commitmentDatedColumnAmounts = amountSums;


            const detailsByDate = transDetails.filter(td => DateTimeFormat.compareDateOnly(td.date, totalCommitmentThruQuarterDate) <= 0);
            const totalTransactionAmount = detailsByDate.reduce((a,b) => a + b.amount, 0);

            cr.totalTransactionAmount = totalTransactionAmount;
        });

    }

    private setAllocationColumns() {
        const grid = this.grid;
        const templateAllocationCols = this.template.allocationsCols.columns;
        const wbInfoAllocationCols = this.workbookInfo.allocationColumns;
        const spaceCol = grid.getNextCol(this.template.closeCommitmentDatedCols.startEndCols.endCol);

        const startCol = grid.getNextCol(spaceCol);
        let currCol = startCol;

        wbInfoAllocationCols.sort((a, b) => a.index - b.index).forEach((allocCol) => {
            templateAllocationCols.push({
                id: allocCol.id,
                code: allocCol.code,
                label: allocCol.label,
                colType: allocCol.transactionType,
                index: allocCol.index,
                col: currCol
            } as AllocationColumn);

            currCol = grid.getNextCol(currCol);
        });

        this.template.allocationsCols.startEndCols = { startCol: startCol, endCol:  grid.getPrevCol(currCol) };
    }

    private setITDColumns(itdCols: TransactionColumn) {
        const firstQuarter = this.workbookInfo.startQuarterNumber;
        let currCol = itdCols.startEndCols.startCol;

        itdCols.columns = [];

        itdCols.columns.push({
            label: stringConstants.beginingBalanceStr,
            col: currCol,
            isUserCol: false,
            allocationCol: undefined,
            metricSign: 0,
            useArkTransactionValues: false
        });
        
        currCol = this.grid.getNextCol(currCol);

        this.workbookInfo.transactionColumns
            .filter(transCol => transCol.quarterNumber === firstQuarter && transCol.index >= 0)
            .sort((a, b) => a.index - b.index).forEach((col) => {
            itdCols.columns.push({
                label: col.label,
                col: currCol,
                isUserCol: false,
                allocationCol: undefined,
                metricSign: col.metricSign,
                useArkTransactionValues: false
            });

            currCol = this.grid.getNextCol(currCol);
        });

        itdCols.columns.push({
            label: stringConstants.endingBalanceStr,
            col: currCol,
            isUserCol: false,
            allocationCol: undefined,
            metricSign: 0,
            useArkTransactionValues: false
        });

        itdCols.startEndCols.endCol = currCol;
    }

    private setTransactionColumns(quarterNumber: number, getColsFromWorkbook: boolean) {
        const grid = this.grid;
        const workbook = this.workbookInfo;
        const transactionCols = this.template.transactionCols;
        const prevQuarterTransationCols = transactionCols.get(quarterNumber-1) as TransactionColumn;
        
        let startCol: Column;
        let newTransactionCols: TransactionColumnDetail[];

        if(getColsFromWorkbook) {

            newTransactionCols = [];
            newTransactionCols.push({
                label: stringConstants.beginingBalanceStr,
                col: '',
                isUserCol: false,
                allocationCol: undefined,
                locked: true,
                metricSign: 0,
                useArkTransactionValues: false
            });

            const wbTransactionCols = workbook.transactionColumns
                .filter(transCol => transCol.quarterNumber === quarterNumber)
                .sort((a, b) => a.index - b.index);

            this.setNewTransactionColumnsDetails(wbTransactionCols, newTransactionCols);
            
            newTransactionCols.push({
                label: stringConstants.endingBalanceStr,
                col: '',
                isUserCol: false,
                allocationCol: undefined,
                locked: true,
                metricSign: 0,
                useArkTransactionValues: false
            });

            if(prevQuarterTransationCols) {
                startCol = prevQuarterTransationCols.startEndCols.endCol;
            } else {
                startCol = this.template.pitdCols.startEndCols.endCol;
            }
        } else {

            newTransactionCols = cloneDeep(prevQuarterTransationCols.columns);
            newTransactionCols.forEach((newCol) => {
                newCol.id = undefined;
                newCol.col='';
                newCol.locked = false;
                newCol.transSubmitError = false;
            });

            startCol = prevQuarterTransationCols.startEndCols.endCol;
        }

        let currCol = grid.getNextCol(startCol);

        const transactionColDetails = newTransactionCols.map((transCol) => {
            currCol = grid.getNextCol(currCol);

            transCol.col = currCol;

            return transCol;
        });

        transactionCols.set(quarterNumber, {
            columns: transactionColDetails,
            startEndCols: { startCol: grid.getNextCol(startCol, 2), endCol: currCol }
        });
    }

    private setInitialLastColAndRow() {
        const grid = this.grid;
        const template = this.template;
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
        const workbook = this.workbookInfo;

        let endCol: string = '';

        if(this.isNew) {
            endCol = grid.getPrevCol(this.getFirstEditableFreeFormCol());
        } else {
            endCol = workbook.lastCol;
        }

        this.template.lastCol = endCol;


        this.template.lastRow = allCommitmentRows[allCommitmentRows.length -1].row;
    }

    private setNewTransactionColumnsDetails(wbTransactionCols: wbTransactionColumn[], newTransactionCols: TransactionColumnDetail[]) {
        const allocationCols = this.template.allocationsCols.columns;

        wbTransactionCols.forEach((transCol, index) =>{
            const allocCol = transCol.allocationCode ? allocationCols.find((x) => x.code === transCol.allocationCode) : undefined;

            newTransactionCols.push({
                id: transCol.id,
                label: transCol.label,
                col: '',
                isUserCol: transCol.isUserColumn,
                allocationCol: allocCol,
                transTypeId: transCol.transTypeId,
                index: index,
                locked: transCol.locked,
                transSubmitError: transCol.transSubmitError,
                metricSign: transCol.metricSign,
                useArkTransactionValues: transCol.useArkTransactionValues
            });
        });
    }

    // start Data building functions


    // cell insert/move functions

    public addTransactionsColumnsFromClientTransTypeRows(clientTransTypes: ClientTransTypeRow[], destinationCol: string) {

        const newTransCols = clientTransTypes.map((typeCol) => {
            return {
                transTypeId: typeCol.id,
                label: typeCol.type,
                isUserCol: false
            } as TransactionColumnDetail; 
        });

        this.addTransactionColumns(newTransCols, destinationCol, true);
    }

    public autoAddTransactionsColumns(colsLength: number, destinationCol: string) {
        const newTransCols: TransactionColumnDetail[] = [];

        for(let c=0; c<colsLength; c++) {
            newTransCols.push({
                transTypeId: generateUUID(),
                label: 'Custom Transaction Column',
                isUserCol: true
            } as TransactionColumnDetail);
        }

        this.addTransactionColumns(newTransCols, destinationCol, false);
    }

    private addTransactionColumns(newTransCols: TransactionColumnDetail[], destinationCol: Column|undefined, addToItd: boolean) {
        if(destinationCol && !this.canAddTransactionCol(destinationCol)) {
            throw 'Invalid transaction columns position';
        }

        const grid = this.grid;
        const wb = this.workbookInfo;
        const template = this.template;
        const lastQuarter = this.getLastQuarter();
        const transCols = template.transactionCols.get(lastQuarter) as TransactionColumn;
        const transColsDetails = transCols.columns as TransactionColumnDetail[];
        
        if(!destinationCol) {
            destinationCol = transCols.startEndCols.endCol;
        }

        const insertIndex = transColsDetails.findIndex((transCol) => transCol.col === destinationCol);
        const insertTransType = transColsDetails[insertIndex].label;

        newTransCols?.forEach((selCol) => {
            selCol.locked = false;
            selCol.transSubmitError = false;

            transColsDetails.splice(insertIndex, 0, selCol);
        });


        let curTransCol = grid.getPrevCol(transCols?.startEndCols.startCol as string);

        transColsDetails.forEach((transCol, index) => {
            curTransCol = grid.getNextCol(curTransCol);
            transCol.col = curTransCol;
            transCol.index = index;
        });

        transCols.startEndCols.endCol = curTransCol;

        const startEndRows = this.getFullStartEndRows();
        const startCol = destinationCol;
        const endCol = this.getLastCol();
        
        grid.moveCells(
            {
                startRow: startEndRows.startRow, 
                endRow: startEndRows.endRow, 
                startCol: startCol, 
                endCol: endCol 
            },
            startEndRows.startRow, grid.getNextCol(destinationCol, newTransCols.length)
        );

        for(let c=insertIndex; c < (insertIndex+newTransCols.length); c++) {
            const newCol = transCols.columns[c];
            
            this.setAllRowsValues(newCol.col, 0, CellFormats.Number_Accounting);
            grid.setCellValue(this.getMainHeaderRow(), newCol.col, newCol.label, CellFormats.Text_Unformatted);
        }

        this.renderTransactionColumnsStyling(lastQuarter);

        if(wb.includeYtd && wb.includeItd) {
            if(addToItd) {
                this.moveItdCols(template.ytdCols, newTransCols.length, false);
                this.moveItdCols(template.itdCols, newTransCols.length, false);

                this.addItdCols(newTransCols, template.ytdCols, insertTransType);

                this.moveItdCols(template.itdCols, newTransCols.length, false);
                this.addItdCols(newTransCols, template.itdCols, insertTransType);
            } else {
                this.moveItdCols(template.ytdCols, newTransCols.length);
                this.moveItdCols(template.itdCols, newTransCols.length);
            }
        }

        if(wb.includeYtd && !wb.includeItd) {
            if(addToItd) {
                this.moveItdCols(template.ytdCols, newTransCols.length, false);
                this.addItdCols(newTransCols, template.ytdCols, insertTransType);
            } else {
                this.moveItdCols(template.ytdCols, newTransCols.length);
            }
        }

        if(!wb.includeYtd && wb.includeItd) {
            if(addToItd) {
                this.moveItdCols(template.ytdCols, newTransCols.length, false);
                this.addItdCols(newTransCols, template.itdCols, insertTransType);
            } else {
                this.moveItdCols(template.itdCols, newTransCols.length);
            }
        }

        this.renderCleanup();
    }

    private addItdCols(newTransCols: TransactionColumnDetail[], itdCols: TransactionColumn, insertTransType: string) {
        const grid = this.grid;
        const insertIndex = itdCols.columns.findIndex((transCol) => transCol.label === insertTransType);
        const destinationCol = itdCols.columns[insertIndex].col;

        newTransCols.forEach((col) => {
            itdCols.columns.splice(insertIndex, 0, col);
        });

        let currCol = grid.getPrevCol(itdCols.startEndCols.startCol);

        itdCols.columns.forEach((itdCol) => {
            currCol = grid.getNextCol(currCol);
            itdCol.col = currCol;
        });

        itdCols.startEndCols.endCol = currCol;


        const startEndRows = this.getFullStartEndRows();
        const startCol = destinationCol;
        const endCol = this.getLastCol();

        grid.moveCells(
            {
                startRow: startEndRows.startRow, 
                endRow: startEndRows.endRow, 
                startCol: startCol, 
                endCol: endCol 
            },
            startEndRows.startRow, grid.getNextCol(destinationCol, newTransCols.length)
        );

        for(let c=insertIndex; c < (insertIndex+newTransCols.length); c++) {
            const newCol = itdCols.columns[c];

            this.setAllRowsValues(newCol.col, 0, CellFormats.Number_Accounting);
            grid.setCellValue(this.getMainHeaderRow(), newCol.col, newCol.label, CellFormats.Text_Unformatted);
        }
        
        this.renderITDColumnsStyling(itdCols);
    }

    private applyTransactionsChanges(updatedWorkbook: Workbook) {
        const lastQtr = this.getLastQuarter();
        const transactionCols = this.template.transactionCols.get(lastQtr); 
        const destinationCol = transactionCols?.startEndCols.endCol;
        let updatedTransactionCols = 
            updatedWorkbook.transactionColumns
            .filter((transCol) => transCol.quarterNumber === lastQtr);
        
        updatedTransactionCols = updatedTransactionCols
                .filter(newTransCol => !transactionCols?.columns.find((transCol) => transCol.transTypeId === newTransCol.transTypeId))
                .filter(newTransCol => newTransCol.id);

        if(updatedTransactionCols.length > 0) {
            const newTransColumnDetail: TransactionColumnDetail[] = [];

            this.setNewTransactionColumnsDetails(updatedTransactionCols, newTransColumnDetail);
            this.addTransactionColumns(newTransColumnDetail, destinationCol, true);
        }
    }

    private applyAllocationChanges(updatedWorkbook: Workbook) {
        const grid = this.grid;
        const wb = this.workbookInfo;
        const destinationCol = grid.getNextCol(this.template.allocationsCols.startEndCols.endCol);
        
        const newWorkbookAllocCols = updatedWorkbook.allocationColumns.filter(newAllocCol => !wb.allocationColumns.find(allocCol => allocCol.code === newAllocCol.code));

        if(newWorkbookAllocCols.length > 0) {
            const newAllocCols = newWorkbookAllocCols.map(newAllocCol => {
                return {
                    id: newAllocCol.id,
                    code: newAllocCol.code,
                    label: this.getAllocationColumnTitle(newAllocCol.label),
                    colType: newAllocCol.transactionType,
                    index: newAllocCol.index,
                    col: ''
                } as AllocationColumn;
            });

            this.addAllocationColumns(newAllocCols, destinationCol);
            this.renderAllocationColumns(newAllocCols);
        }
    }

    public canAddTransactionCol(col: string) {
        const grid = this.grid;

        const lastQtr = this.getLastQuarter();
        const lastQtrTransStartEndCols = this.template.transactionCols.get(lastQtr)?.startEndCols as StartEndCols;
        const startCol = grid.getNextCol(lastQtrTransStartEndCols.startCol);

        let endCol = grid.getPrevCol(lastQtrTransStartEndCols.endCol);

        endCol = grid.getNextCol(endCol);

        return grid.isColBetween(col, startCol, endCol);
    }

    public rolloverTransactionColumns(investorCommitmentList: InvestorCommitment[], transTypeSums:  Map<number, TransactionTypeQuarterlySum[]>, loadComplete: () => void) {
        if(!this.canGenerateRolloverTransactionColumns()) {
            throw 'Too many quarters in rollover';
        }

        const template = this.template;
        const wb = this.workbookInfo;
        const prevQuarterNumber = this.getLastQuarter();
        const newQuarterNumber = prevQuarterNumber + 1;
        const quarterColumns = template.transactionCols.get(prevQuarterNumber)!.columns;
        const startClearCol = template.transactionCols.get(prevQuarterNumber)?.startEndCols.startCol!;
        const moveSize = quarterColumns!.length + 1;
        const waitTime = 10;
        const _this = this; // gives access to inner functions

        this.investorCommitments = investorCommitmentList;

        this.renderHeaderInfo(wb);

        const newInvestors = this.adjustForInvestorChanges();

        if(newInvestors.length === 0) {
            this.clearSumFormulas(startClearCol);
        }

        // Using setTimout() functions to prevent the browser 'Page Unresponsive' from appearing
        setTimeout(setDataStructures, waitTime);

        function setDataStructures() {
            _this.setCloseCommitmentDatedColumns(newQuarterNumber);
            _this.moveCellsForDatedCommitmentColumns(true, false);
            _this.renderDatedCommitmentColumns();

            if(wb.includeItd) {
                _this.moveItdCols(template.itdCols, moveSize);
            }

            if(wb.includeYtd) {
                _this.moveItdCols(template.ytdCols, moveSize);
            }
            
            _this.setTransactionColumns(newQuarterNumber, false);

            setTimeout(renderRolloverGrid, waitTime);
        }

        function renderRolloverGrid() {
            _this.rendeFullGrid(true, newInvestors, transTypeSums);

            setTimeout(renderRolloverCleanUp, waitTime);
        }
        
        function renderRolloverCleanUp() {
            _this.renderAllColumnsStyling();
            
            setTimeout(loadComplete, 1000);
        } 
    }


    public AutoAddAllocationColumns(colsCount: number, destinationCol: string) {
        const newAllocCols: AllocationColumn[] = [];

        for(let c = 0; c < colsCount; c++) {
            const newAllocCol: AllocationColumn = {
                label: 'Custom Allocaion Column',
                code: generateUUID(),
                col: '',
                colType: AllocationColumnTypes.CUSTOM_FIELD,
                index: -1
            };

            newAllocCols.push(newAllocCol);
        }

        this.addAllocationColumns(newAllocCols, destinationCol);
    }

    public addAllocationColumns(allocationColumns: AllocationColumn[], destinationCol: string) {
        if(!this.canAddAllocationColumn(destinationCol)) {
            throw 'Invalid allocation columns position';
        }

        const grid = this.grid;
        const wb = this.workbookInfo;
        const template = this.template;
        const startEndRows = this.getFullStartEndRows();
        const allocCols = template.allocationsCols.columns;

        let currDestCol = destinationCol;

        allocationColumns.forEach((newAllocCol) => {
            const index = allocCols.findIndex((allocCol) => allocCol.col === currDestCol);

            if(index < 0) {
                allocCols.push(newAllocCol);
            } else  {
                allocCols.splice(index, 0, newAllocCol);
            }

            currDestCol = grid.getNextCol(currDestCol);
        });

        const startCol = destinationCol;
        const endCol = this.getLastCol();
        let currCol = grid.getPrevCol(template.allocationsCols.startEndCols.startCol);

        allocCols.forEach((allocCol, index) => {
            currCol = grid.getNextCol(currCol);
            allocCol.col = currCol;
            allocCol.index = index;
        });

        template.allocationsCols.startEndCols.endCol = currCol;
        
        grid.moveCells(
            {
                startRow: startEndRows.startRow, 
                endRow: startEndRows.endRow, 
                startCol: startCol, 
                endCol: endCol 
            },
            startEndRows.startRow, grid.getNextCol(destinationCol, allocationColumns.length)
        );

        this.renderAllocationColumnsStyling();

        allocCols.forEach((allocCol) => {
            grid.setCellValue(this.getMainHeaderRow(), allocCol.col, allocCol.label, CellFormats.Text_Unformatted);
        });
        

        //  move rest of columns

        this.moveItdCols(template.pitdCols, allocationColumns.length);
        this.moveTransactionCols(allocationColumns.length);

        if(wb.includeYtd) {
            this.moveItdCols(template.ytdCols, allocationColumns.length);
        }

        if(wb.includeItd) {
            this.moveItdCols(template.itdCols, allocationColumns.length);
        }

        this.renderCleanup();
    }

    private moveAllocationCols(moveCount: number, setStyling = true) {
        const grid = this.grid;
        const allocationCols = this.template.allocationsCols;

        const startEndCols = allocationCols.startEndCols;
        const newColStart = grid.getNextCol(startEndCols.startCol, moveCount);

        startEndCols.startCol = newColStart;
        let currCol = grid.getPrevCol(startEndCols.startCol);

        allocationCols.columns.forEach(c => {
            currCol = grid.getNextCol(currCol);
            c.col = currCol;
        });

        startEndCols.endCol = currCol;

        if(setStyling) {
            this.renderAllocationColumnsStyling();
        }
    }

    private moveItdCols(itdCol: TransactionColumn, moveCount: number, setStyling = true) {
        const grid = this.grid;
        const startEndCols = itdCol.startEndCols;
        const columns = itdCol.columns;

        startEndCols.startCol = grid.getNextCol(startEndCols.startCol, moveCount);
        startEndCols.endCol = grid.getNextCol(startEndCols.endCol, moveCount);

        columns.forEach((itdCol) => {
            itdCol.col = grid.getNextCol(itdCol.col, moveCount);
        });

        if(setStyling) {
            this.renderITDColumnsStyling(itdCol);
        }
    }

    private moveTransactionCols(moveCount: number, setStyling = true) {
        const grid = this.grid;
        const transCols = this.template.transactionCols;
        const firstCol = this.getFirstQuarter();

        for(let i=firstCol; i <= transCols.size; i++) {
            const quarterTransCols = transCols.get(i) as TransactionColumn;

            quarterTransCols.startEndCols.startCol = grid.getNextCol(quarterTransCols.startEndCols.startCol, moveCount);
            quarterTransCols.startEndCols.endCol = grid.getNextCol(quarterTransCols.startEndCols.endCol, moveCount);
            quarterTransCols.columns.forEach((transCol) => {
                transCol.col = grid.getNextCol(transCol.col, moveCount);
            });

            if(setStyling) {
                this.renderTransactionColumnsStyling(i);
            }
        }
    }

    private moveCellsForDatedCommitmentColumns(moveDateStructures: Boolean, clearSumFormulasIfNecessary: boolean) {
        const grid = this.grid;
        const wb = this.workbookInfo;
        const template = this.template;
        const closeCommitmentCol = template.closeCommitmentCol;
        const closeCommitmentDatedCols = this.template.closeCommitmentDatedCols;

        const oldColsLength = this.getNumOfDatedCommitmentColsFromWB();
        const newColsLength = closeCommitmentDatedCols.columns.length;
        
        if(oldColsLength === newColsLength ) {
            return 0; // if we are here then current space in wb is set to nothing to move
        }

        if(clearSumFormulasIfNecessary){
            this.clearSumFormulas('A');
        }

        const moveLength = newColsLength - oldColsLength;

        const moveColFrom = grid.getNextCol(closeCommitmentCol, oldColsLength);
        const moveColTo = grid.getNextCol(closeCommitmentCol, newColsLength);

        if(moveDateStructures) {
            this.moveAllocationCols(moveLength, false);
            this.moveItdCols(template.pitdCols, moveLength, false);
            this.moveTransactionCols(moveLength, false);

            if(wb.includeYtd) {
                this.moveItdCols(template.ytdCols, moveLength, false);
            }

            if(wb.includeItd) {
                this.moveItdCols(template.itdCols, moveLength, false);
            }
        }
        
        const startEndRows = this.getFullStartEndRows();
        const endCol = this.getLastCol();
        const endRow = this.getLastRow();

        grid.moveCells({
                startCol: moveColFrom, 
                endCol,
                startRow: startEndRows.startRow,
                endRow
            },  
            startEndRows.startRow, moveColTo
        );

        return moveLength;
    }

    // End cell insert/move functions


    // Start util functions

    public getFirstQuarter() {
        return this.workbookInfo.startQuarterNumber;
    }
    
    public getLastQuarter() {
        let lastQuarter: number = 1;
        const transactionCols = this.template.transactionCols;

        for(let i=1; i <= 4; i++) {
            if(transactionCols.get(i)) {
                lastQuarter = i;
            }
        }

        return lastQuarter;
    }

    public getFirstEditableFreeFormCol() {
        const grid = this.grid;
        const template = this.template;
        const workbook = this.workbookInfo;
        let endCol: string = '';

        if(workbook.includeItd) {
            endCol = template.itdCols.startEndCols.endCol;
        } else if(workbook.includeYtd && !workbook.includeItd) {
            endCol = template.ytdCols.startEndCols.endCol;
        } else if(!workbook.includeYtd && !workbook.includeItd) {
            endCol = template.transactionCols.get(template.transactionCols.size-1)?.startEndCols.endCol as string;
        }

        return grid.getNextCol(endCol, 2);
    }

    private getLastCol() {
        const wb = this.workbookInfo;
        const wbLastCol = this.getFirstEditableFreeFormCol();

        let lastCol = this.template.lastCol;

        if(this.compareCols(lastCol, wbLastCol) < 0) {
            lastCol = wbLastCol;
        }

        return lastCol;
    }

    private getLastRow() {
        return this.template.lastRow;
    }

    private getFullStartEndRows() {
        const template = this.template;

        const startRow = template.quarterHeaderRow;
        const endRow = this.getLastRow();

        return {
            startRow,
            endRow
        };
    }

    public getFirstEditableRow() {
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;

        return allCommitmentRows[allCommitmentRows.length-1].row + 1;
    }

    // End util functions


    // Start workbook status functions

    public isWorkbookReadyforNewWB() {
        if(this.workbookInfo.frequency === stringConstants.byQuarterStr) {
            return this.getLastQuarter() === 4;
        } else {
            return true;
        }
    }

    public isCellEditableRange(startCol: string, startRow: number, numOfCols: number, numOfRows: number) {
        const grid = this.grid;
        const invalidCells: string[] = [];

        for(let c=0; c< numOfCols; c++) {
            const col = grid.getNextCol(startCol, c);

            for(let r=0; r<numOfRows; r++) {
              const row = startRow+r;
              const isEditable = this.isCellEditable(row, col);

              if(!isEditable) {
                const badCell = `${col}${row}`;

                invalidCells.push(badCell);
              }
            }
        }

        return invalidCells;
    }

    public isCellEditable(row: Row, col: Column) {
        const grid = this.grid;
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;

        if(row < this.getMainHeaderRow()) {
            return false;
        }

        const isAllocCol = this.isAllocationColumn(col);
        const freeRowStart = allCommitmentRows[allCommitmentRows.length-1].row+1;

        if(row > freeRowStart) {
            return true;
        }

        const freeColStart = this.getFirstEditableFreeFormCol();
        const compCol = grid.compareCols(freeColStart, col);

        if(compCol < 0) {
            return true;
        }

        if(isAllocCol) {
            return true;
        }

        if(this.isTransactionColumnEditable(col)) {
            return true;
        }
        
        return false;
    }   
    
    public canAddAllocationColumn(destCol: string) {
        const grid = this.grid;
        const allocationsCols = this.template.allocationsCols;
        const startEndCols = allocationsCols.startEndCols;
        const firstCustCol = allocationsCols.columns.find((col) => col.colType === AllocationColumnTypes.CUSTOM_FIELD);

        if(firstCustCol) {
            const firstCol = firstCustCol.col;
            const lastCol =  grid.getNextCol(startEndCols.endCol);

            return grid.isColBetween(destCol, firstCol, lastCol);
        } else {
            return grid.getNextCol(startEndCols.endCol) === destCol;
        }   
    }
    
    public isTransactionColumnEditable(col: Column) {
        const transColSets = this.template.transactionCols;
        const firstQuarter = this.getFirstQuarter();
        
        for(let i = firstQuarter; i <= transColSets.size+firstQuarter; i++) {
            const transColSet = transColSets.get(i);
            const transCol = transColSet?.columns
                .filter(c => c.label !== stringConstants.beginingBalanceStr && c.label !== stringConstants.endingBalanceStr)
                .find(transCol => transCol.col === col);

            if(transCol) {
                return !transCol.locked;
            }
        }

        return false;
    }

    public isAllocationColumn(col: Column) {
        const grid = this.grid;
        const allocationsCols = this.template.allocationsCols;
        const startEndCols = allocationsCols.startEndCols;

        return grid.isColBetween(col, startEndCols.startCol, startEndCols.endCol);
    }

    public canGenerateRolloverTransactionColumns() {
        if(this.workbookInfo.frequency === stringConstants.byQuarterStr) {
            return this.template.transactionCols.size < 4;
        } else {
            return false;
        }
    }

    // End workbook status functions


    // Start get/set valuea Util functions

    private getLongDateForQuarter(year: number, quarterNumber?: number) {
        if(quarterNumber) {
            const startEndDates = getQuarterStartEndDates(quarterNumber, year);
            const dateStr = DateTimeFormat.longDate(startEndDates.endDate);

            return dateStr;
        } else {
            return year.toString();
        }
    }

    private getAllocationColumnTitle(label: string){
        const wb = this.workbookInfo;

        const longStartDateStr = this.getLongDateForQuarter(wb.startQuarterYear, wb.frequency === stringConstants.byQuarterStr ? wb.startQuarterNumber : undefined);
        const colHeader = label.replace(stringConstants.dynamicDateStr, longStartDateStr);

        return colHeader;
    }

    public getTransactionTypeValues(quarter: number, transactionTypeId: string) {
        const grid = this.grid;
        const transCol = this.template.transactionCols.get(quarter)?.columns.find((c) => c.transTypeId === transactionTypeId);
        const transTypeName = transCol?.label as string;
        const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows.filter(inv => inv.rowType === InvestorCommitmentRowType.Investor);
        const transValues: SubmitToPcapTransaction[] = [];

        allCommitmentRows.forEach((investorRow) => {
            const cellNum = grid.getCellValue(investorRow.row, transCol?.col as string);
            const parseNum = parseFloat(cellNum);
            let num = isNaN(parseNum) ? 0 : parseNum;

            if(num !== 0) {
                if(transCol?.metricSign === -1) {
                    num *= -1;
                }

                transValues.push({
                    name: transTypeName,
                    investor: investorRow.investorId,
                    balance: parseFloat( num.toFixed(2) )
                });
            }
        });

        return transValues;
    }

    public getTransactionTypeGLRconcileStatus(quarter: number, transactionTypeId: string) {
        try {
            const grid = this.grid;
            const transCol = this.template.transactionCols.get(quarter)?.columns.find((c) => c.transTypeId === transactionTypeId);
            const allCommitmentRows = this.template.investorCommitmentRows.allCommitmentRows;
            const partnerTotalRow = allCommitmentRows.find(cr => cr.rowType === InvestorCommitmentRowType.PartnerTotal) as InvestorCommitmentRow;

            const partnerTotalVal = grid.getCellValue(partnerTotalRow.row+1, transCol?.col as Column);
            const partnerTotalNum =  parseFloat( partnerTotalVal.toFixed(2) );  // round to nearest cent because sometimes we get very tiny (scientific notation) numbers

            if(isNaN(partnerTotalNum)) {
                return false;
            }

            return partnerTotalNum === 0;
        } catch {
            return false;
        }
    }

    private getUniqueCommitmentDates(investorCommits: InvestorCommitment[], startDate: Date, endDate: Date) {
        const columnDates: Date[] = [];
        
        for(let i = 0; i < investorCommits.length; i++) {
            const transDetails = investorCommits[i].transactionsDetails
                .filter(td => DateTimeFormat.compareDateOnly(startDate, td.date) <= 0 && DateTimeFormat.compareDateOnly(endDate, td.date) >= 0);

            for(let j = 0; j < transDetails.length; j++) {
                const currDt = DateTimeFormat.getDateOnly(transDetails[j].date);
                const colDate = columnDates.find(cd => DateTimeFormat.compareDateOnly(currDt, cd) === 0 );

                if(!colDate) {
                    columnDates.push(currDt);
                }
            }
        }

        columnDates.sort((a,b) => a.getTime()-b.getTime());

        return columnDates;
    }

    private getNumOfDatedCommitmentColsFromWB() {
        const grid = this.grid;
        const headerRow = this.getMainHeaderRow();

        let count = 0;
        let continueLoop = true;
        let currCol = this.template.closeCommitmentCol;

        do {
            currCol = grid.getNextCol(currCol);
            const currCellVal = grid.getCellValue(headerRow, currCol) as string;

            if(currCellVal.startsWith(stringConstants.commitmentCloseOnStartStr)) {
                count++;
            } else {
                continueLoop = false;
            }
        } while (continueLoop);

        return count;
    }

    public getReportName() {
        return this.workbookInfo.reportName;
    }

    public getFundId() {
        return this.workbookInfo.fund.id;
    }

    public getYear() {
        return this.workbookInfo.startQuarterYear;
    }

    public getWorkbookFrequency() {
        return this.workbookInfo.frequency;
    }

    public getInvestorIDs() {
        return this.investorCommitments.map(i => i.investorId);
    }

    public getWorkbookInfo() {
        const workbook = this.workbookInfo;
        const template = this.template;

        workbook.version = WorkbookVersion;

        workbook.lastRow = this.getLastRow();
        workbook.lastCol = this.getLastCol();
        
        workbook.allocationColumns = template.allocationsCols.columns.map((col) => {
            return {
                id: col.id,
                code: col.code,
                index: col.index,
                label: col.label,
                transactionType: col.colType
            };
        });

        const firstQuarter = this. getFirstQuarter();
        const lastQuarter = this.getLastQuarter();

        workbook.transactionColumns = [];
        for(let q = firstQuarter; q <= lastQuarter; q++) {
            const currQuarterTransCols = template.transactionCols.get(q)?.columns as TransactionColumnDetail[];

            currQuarterTransCols.filter(c => c.label !== stringConstants.beginingBalanceStr && c.label !== stringConstants.endingBalanceStr).forEach((srcCol, index) => {
                workbook.transactionColumns.push({
                    id: srcCol.id,
                    label: srcCol.label,
                    index: index,
                    allocationCode: srcCol.allocationCol?.code,
                    transSubmitError: srcCol.transSubmitError as boolean,
                    locked: srcCol.locked as boolean,
                    quarterNumber: q,
                    transTypeId: srcCol.transTypeId as string,
                    isUserColumn: srcCol.isUserCol,
                    metricSign: srcCol.metricSign,
                    useArkTransactionValues: srcCol.useArkTransactionValues
                });
            });
        }

        workbook.investorRows = template.investorCommitmentRows.allCommitmentRows.map(cr => {
            return {
                id: cr.id,
                investorId: cr.investorId,
                investorName: cr.investorName,
                investorType: cr.investorType,
                row: cr.row,
                rowType: cr.rowType,
                deleted: cr.deleted
            } as InvestorRow;
        });
        

        return cloneDeep(workbook);
    }

    public getSpreadsheetGrid() {
        return this.grid;
    }

    public saveSpreadsheet() {
        this.grid.saveSpreadsheet();
    }

    public getMainHeaderRow() {
        return this.template.mainHeaderRow;
    }

    public setReportName(newReportName: string) {
        this.workbookInfo.reportName = newReportName;
        this.renderHeaderInfo();
    }

    public setUpdatedValuesfromSave(updatedWorkbook: Workbook) {
        this.workbookInfo = updatedWorkbook;
        const transCols = this.template.transactionCols;
        const investorRows = this.template.investorCommitmentRows.allCommitmentRows;

        this.isNew = false;
        
        updatedWorkbook.transactionColumns.forEach(updatedTransCol => {
            const transColumnDetails = transCols.get(updatedTransCol.quarterNumber)?.columns;

            if(transColumnDetails) {
                const transColumnDetail = transColumnDetails
                    .filter(col => col.label !== stringConstants.beginingBalanceStr && col.label !== stringConstants.endingBalanceStr)
                    .find(col => col.transTypeId === updatedTransCol.transTypeId);

                transColumnDetail!.id = updatedTransCol.id;
            }
        });

        
        updatedWorkbook.investorRows.forEach(updatedRow => {
            const investorRow = investorRows.find(r => r.row === updatedRow.row);

            if(investorRow) {
                investorRow.id = updatedRow.id;
            }
        });

    }
    
    public editCell(row: Row, col: Column, value: string) {
        const template = this.template;

        if(row === this.getMainHeaderRow() && this.isAllocationColumn(col)) {
            this.editAllocationTitle(col, value);
            return;
        }

        if(row > this.getLastRow()) {
            template.lastRow = row;
        }

        if(this.grid.compareCols(col, template.lastCol) > 0) {
            template.lastCol = col;
        }
    }

    public editAllocationTitle(col: Column, label: string) {
        const allocCols = this.template.allocationsCols.columns;
        const allocCol = allocCols.find((ac) => ac.col === col);
        
        if(allocCol) {
            allocCol.label = label;
        }
    }
    
    public updateTransactionColumnsLockStatus(updatedWorkbook: Workbook) {
        const transCols = this.template.transactionCols;

        updatedWorkbook.transactionColumns.forEach(updatedTransCol => {
            const transColumnDetails = transCols.get(updatedTransCol.quarterNumber)?.columns;

            if(transColumnDetails) {
                const transColumnDetail = transColumnDetails
                    .filter(col => col.label !== stringConstants.beginingBalanceStr && col.label !== stringConstants.endingBalanceStr)
                    .find(col => col.transTypeId === updatedTransCol.transTypeId);

                transColumnDetail!.locked = updatedTransCol.locked;
            }
        });
    }

    public applyWorkbookConfigChanges(updatedWorkbook: Workbook) {
        this.renderHeaderInfo(updatedWorkbook);

        this.applyAllocationChanges(updatedWorkbook);
        this.applyTransactionsChanges(updatedWorkbook);

        this.workbookInfo = updatedWorkbook;

        this.renderCleanup();
    }

    public compareCols(a: Column, b: Column) {
        return this.grid.compareCols(a, b);
    }

    // End get/set valuea Util functions
}